import { useState } from "react";
import { Columns3, Download, Link2, Lock } from "lucide-react";
import {
  Button,
  Chip,
  ColumnPicker,
  ColumnPickerAddButton,
  GroupByPicker,
  MetricCard,
  SegmentedControl,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
  cn,
} from "@fox1ck-org/ui";
import { toast } from "@fox1ck-org/ui/toast";
import { ReportViewDefinition } from "@gen/tracker/v1/reporting_pb";

import { DASH, formatDayRange, formatInt, formatPct } from "@/lib/format";
import { MAX_LEVELS, type DimFilter, type MetricCond } from "@/lib/report";
import type { CompareMode, ConversionBasis } from "@/lib/period";
import { PRESET_SETS } from "@/lib/campaigns-table";
import { useT, type TKey } from "@/lib/i18n";
import { useCreateReportView } from "@/lib/services/report-view-service";
import { PICKER_LAYOUT, nextOrder, pickerGroups } from "@/lib/column-picker";
import { toastFailure } from "@/lib/plan";
import { activeSet, betterOf, BUILTIN_SETS, cellText, columnLabel, deltaText, deltaTone, reportColumns, reportPickerEntries, setKeys } from "./columns";
import { FilterPanel, DIM_OP_WORD, METRIC_OP_SIGN } from "./filter-panel";
import { FormulaDialog } from "./formula-dialog";
import { NameDialog } from "./saved-views";
import type { ReportEngine } from "./engine";

/**
 * «Группировка Кампания › Объявление › Страна + уровень · Конверсии по · Сравнить
 * · + Фильтр» (доски Report, ReportDims): the levels on the package's
 * GroupByPicker, the basis and the comparison writing the Top bar's own keys.
 */
/**
 * `compact` (доска MReport): the levels and «+ Фильтр» only — the date a
 * conversion counts on and «Сравнить» are the period's, in its popover.
 */
export function ReportGrouping({ engine, filterTrigger = true, compact, className }: { engine: ReportEngine; filterTrigger?: boolean; compact?: boolean; className?: string }) {
  const t = useT();
  const { state } = engine;
  const p = state.params;
  const setPeriod = (patch: { compare?: CompareMode; basis?: ConversionBasis }) =>
    p.setPeriod({ preset: p.preset, from: p.from, to: p.to, compare: patch.compare ?? p.compare, basis: patch.basis ?? p.basis });
  return (
    <div className={cn("flex min-h-8 flex-wrap items-center gap-x-3 gap-y-2", className)}>
      <GroupByPicker
        dimensions={engine.dimensions}
        groups={engine.groups}
        folds={engine.folds}
        value={state.dims}
        onChange={state.setDims}
        max={MAX_LEVELS}
        labels={{
          title: t("rep.grouping"),
          add: t("rep.level.add"),
          search: t("rep.level.search"),
          keyboardHint: t("rep.level.keys"),
          order: t("rep.level.order"),
          level: (n) => t("rep.level.n", { n }),
          count: (n, max) => t("rep.level.count", { n, max }),
          candidate: (label) => `+ ${label}`,
          remove: (label) => t("rep.level.remove", { name: label }),
          move: (label) => t("rep.level.move", { name: label }),
          empty: t("rep.nothingFound"),
          foldCount: (total, chosen) => (chosen > 0 ? t("rep.fold.chosen", { chosen, total }) : String(total)),
          fold: (label, open) => t(open ? "rep.fold.collapse" : "rep.fold.expand", { name: label }),
        }}
      />
      {compact ? null : (
        <>
          <span aria-hidden className="h-5 w-px bg-border" />
          <span className="whitespace-nowrap text-xs text-muted-foreground">{t("rep.basis")}</span>
          <SegmentedControl
            size="sm"
            aria-label={t("rep.basis")}
            value={engine.basis}
            disabled={engine.time}
            onValueChange={(b: ConversionBasis) => setPeriod({ basis: b })}
            options={[
              { value: "lead", label: t("rep.basis.lead") },
              { value: "conv", label: t("rep.basis.conv") },
              { value: "click", label: t("rep.basis.click") },
            ]}
          />
          <span aria-hidden className="h-5 w-px bg-border" />
          <label className="inline-flex items-center gap-2 whitespace-nowrap text-sm">
            <Switch checked={p.compare !== "off" && !engine.time} disabled={engine.time} onCheckedChange={(on) => setPeriod({ compare: on ? "previous" : "off" })} />
            {t("rep.compare")}
          </label>
        </>
      )}
      <span className="flex-1" />
      {filterTrigger ? <FilterPanel engine={engine} /> : null}
    </div>
  );
}

/**
 * A filter in words: on a chip «Страна RO, PL» (a country is its code there,
 * its name in the lists), in a sentence «Страна — Танзания» (доска
 * ReportEmpty). A metric condition reads the same in both: «Профит > 0».
 */
export function useFilterWords(engine: ReportEngine) {
  const t = useT();
  const { names } = engine;
  const short = (key: string, v: string) => (key === "country" ? v : names.name(key, v));
  const dimChip = (d: DimFilter) => `${engine.dimLabel(d.key)} ${DIM_OP_WORD(t, d.op)}${d.values.map((v) => short(d.key, v)).join(", ")}`;
  const dimPhrase = (d: DimFilter) => `${engine.dimLabel(d.key)} ${DIM_OP_WORD(t, d.op) || "— "}${d.values.map((v) => names.name(d.key, v)).join(", ")}`;
  const metricChip = (m: MetricCond) => {
    const c = reportColumns(engine.schema).find((x) => x.key === m.key);
    const pct = !!c && (c.format === "pct0" || c.format === "pct1" || c.format === "roi");
    const n = Number(m.value);
    return `${c ? columnLabel(t, c) : m.key} ${METRIC_OP_SIGN[m.op] ?? ""} ${pct ? formatPct(n, { digits: 0 }) : formatInt(n)}`;
  };
  return { dimChip, dimPhrase, metricChip };
}

/** «Фильтры Страна RO, PL × · Профит < 0 × · + Фильтр · Сбросить все» (доска ReportFilter). */
export function ReportFilterRow({ engine, className }: { engine: ReportEngine; className?: string }) {
  const t = useT();
  const { state, pinned } = engine;
  const f = state.filters;
  const { dimChip, metricChip } = useFilterWords(engine);
  if (!pinned.length && !f.dims.length && !f.metrics.length) return null;
  return (
    <div className={cn("flex min-h-8 flex-wrap items-center gap-2", className)}>
      <span className="text-xs text-muted-foreground">{t("rep.filters")}</span>
      {pinned.map((d, i) => (
        <span key={`p${i}`} className="inline-flex h-7 items-center gap-1.5 rounded-full border border-border bg-surface-2 px-3 text-sm" title={t("rep.pinned.hint")}>
          {dimChip(d)}
          <Lock className="size-3 text-muted-foreground" aria-label={t("rep.pinned")} />
        </span>
      ))}
      {f.dims.map((d, i) => (
        <Chip key={`d${i}`} label={dimChip(d)} onRemove={() => state.setFilters({ ...f, dims: f.dims.filter((_, j) => j !== i) })} />
      ))}
      {f.metrics.map((m, i) => (
        <Chip key={`m${i}`} label={metricChip(m)} onRemove={() => state.setFilters({ ...f, metrics: f.metrics.filter((_, j) => j !== i) })} />
      ))}
      <FilterPanel engine={engine} />
      <span className="flex-1" />
      {f.dims.length || f.metrics.length ? (
        <Button variant="ghost" size="sm" onClick={() => state.setFilters({ dims: [], metrics: [] })}>
          {t("rep.filters.reset")}
        </Button>
      ) : null}
    </div>
  );
}


/**
 * «Колонки: Деньги» (доска ReportColumns): the package's ColumnPicker as a
 * popover under its button — «Найти метрику», the sets as one row (the four
 * built-in, the workspace's saved ones, «Мои»), the columns in two columns
 * Трафик · Конверсии | Деньги · Свои with «3 из 9» beside each heading, each
 * its short name with what it counts on the right (lib/column-picker, the
 * same as the campaign list's picker) and, when shown, its grip: a column is
 * dragged within its own group, the table's band. A ticked column goes in
 * beside its group's shown neighbour. «+ Своя формула» · «Сбросить» ·
 * «Сохранить как набор» along the bottom. A set picked is the set's id in the
 * address; a tick makes the columns the reader's own («Мои»). «Сохранить как
 * набор» is a ReportView of surface `columns`.
 */
/** `iconOnly` (≤1440, the rail at 56): each header button is its icon, its words the name and the hint. */
export function ColumnsButton({ engine, iconOnly }: { engine: ReportEngine; iconOnly?: boolean }) {
  const t = useT();
  const { state, schema } = engine;
  const [open, setOpen] = useState(false);
  const [formula, setFormula] = useState(false);
  const [naming, setNaming] = useState(false);
  const all = reportColumns(schema);
  const keys = state.metrics;
  const visibility = Object.fromEntries(all.map((c) => [c.key, keys.includes(c.key)]));
  const sets = [
    ...BUILTIN_SETS.map((id) => ({ id, name: t(`rep.set.${id}` as TKey) })),
    ...engine.columnSets.map((v) => ({ id: v.id, name: v.name })),
    { id: MINE, name: t("rep.set.mineChip") },
  ];
  const active = activeSet(keys, engine.columnSets);
  const activeName = (active && sets.find((s) => s.id === active)?.name) || t("rep.set.own");
  const groups = pickerGroups(reportPickerEntries(all, t), (g) => t(`rep.band.${g}` as TKey));
  const create = useCreateReportView();
  const setOf = (id: string) => state.setColumns({ set: id, metrics: setKeys(id, engine.columnSets) ?? PRESET_SETS.money });

  // Keep the reader's order; a newly ticked column goes in beside its group's shown neighbour.
  const onVisibility = (next: Record<string, boolean>) => state.setColumns({ set: "", metrics: nextOrder(keys, next, groups) });
  const label = t("rep.columns", { set: activeName });

  return (
    <>
      <ColumnPicker
        open={open}
        onOpenChange={setOpen}
        trigger={
          <Button variant="secondary" aria-expanded={open} {...iconProps(iconOnly, label)}>
            <Columns3 className="size-4" strokeWidth={1.5} />
            {iconOnly ? null : label}
          </Button>
        }
        search
        counts
        groups={groups}
        visibility={visibility}
        onVisibilityChange={onVisibility}
        sets={sets}
        activeSetId={active ?? MINE}
        // «Мои» keeps what is on screen as the reader's own.
        onSelectSet={(id) => (id === MINE ? state.setColumns({ set: "", metrics: keys }) : setOf(id))}
        onResetSet={() => setOf(active ?? (state.set || "money"))}
        onSaveSet={() => setNaming(true)}
        // The board's order lives in the groups: each shown column carries its grip in its own band.
        reorder="groups"
        order={keys}
        onOrderChange={(next) => state.setColumns({ set: "", metrics: next.filter((k) => keys.includes(k)) })}
        layout={PICKER_LAYOUT}
        footer={<ColumnPickerAddButton onClick={() => setFormula(true)}>{t("rep.formula.add")}</ColumnPickerAddButton>}
        labels={{
          title: t("rep.columns.title"),
          description: t("rep.columns.description"),
          sets: t("rep.columns.sets"),
          setsCaption: t("rep.columns.caption"),
          save: t("rep.columns.save"),
          reset: t("rep.columns.reset"),
          empty: t("rep.columns.empty"),
          close: t("rep.close"),
          search: t("rep.columns.search"),
          clearSearch: t("rep.columns.clearSearch"),
          noMatches: (query) => t("rep.columns.noMatches", { query }),
          groupCount: (shown, total) => t("rep.columns.count", { shown, total }),
          move: (name) => t("camps.cols.move", { name }),
          moved: (name, position, total) => t("camps.cols.moved", { name, position, total }),
        }}
      />
      <NameDialog
        open={naming}
        onOpenChange={setNaming}
        title={t("rep.columns.save")}
        initial={t("rep.set.mine")}
        onSubmit={(name) =>
          create.mutate(
            { name, workspaceShared: false, definition: new ReportViewDefinition({ surface: "columns", metricKeys: keys }) },
            {
              onSuccess: (v) => {
                setNaming(false);
                toast.success(t("rep.set.saved", { name }));
                if (v) state.setColumns({ set: v.id, metrics: keys });
              },
              onError: (e) => toastFailure(e, t("rep.saveFailed")),
            },
          )
        }
      />
      <FormulaDialog schema={schema} rawTotals={engine.rawTotals} open={formula} onOpenChange={setFormula} onCreated={(key) => state.setColumns({ set: "", metrics: [...keys, key] })} />
    </>
  );
}

/** The chip of the reader's own columns; never an id the address carries. */
const MINE = "mine";

export function CsvButton({ engine, stem, iconOnly }: { engine: ReportEngine; stem?: string; iconOnly?: boolean }) {
  const t = useT();
  return (
    <Button
      variant="secondary"
      {...iconProps(iconOnly, "CSV")}
      onClick={() => {
        if (!engine.tree.length) return void toast.error(t("rep.csvEmpty"));
        engine.exportCsv(stem);
      }}
    >
      <Download className="size-4" strokeWidth={1.5} />
      {iconOnly ? null : "CSV"}
    </Button>
  );
}

export function LinkButton({ iconOnly }: { iconOnly?: boolean }) {
  const t = useT();
  return (
    <Button
      variant="secondary"
      {...iconProps(iconOnly, t("rep.link"))}
      onClick={() => {
        void navigator.clipboard?.writeText(window.location.href);
        toast.success(t("rep.linkCopied"));
      }}
    >
      <Link2 className="size-4" strokeWidth={1.5} />
      {iconOnly ? null : t("rep.link")}
    </Button>
  );
}

/**
 * «Сравнить 14.10 – 20.10 против 07.10 – 13.10 ▾ · Рядом | Только Δ»
 * (доска ReportCompare). The other period is the Top bar's `cmp`; the way the
 * table shows it is the report's `cmpv`.
 */
export function CompareBar({ engine }: { engine: ReportEngine }) {
  const t = useT();
  const { state, compare } = engine;
  const p = state.params;
  // Side by side is for one level (доска ReportCompare); a tree carries Δ under each value (доска Report).
  if (!compare || (state.compareView !== "side" && state.dims.length > 1)) return null;
  const live = p.to === new Date().toISOString().slice(0, 10);
  const sortCol = engine.columns.find((c) => c.key === state.sort.key);
  return (
    <div className="flex min-h-8 flex-wrap items-center gap-2">
      <span className="text-xs text-muted-foreground">{t("rep.compare.short")}</span>
      <span className="inline-flex h-7 items-center rounded-full bg-foreground px-3 font-mono text-sm text-background">{formatDayRange(p.from, p.to)}</span>
      <span className="text-xs text-muted-foreground">{t("rep.compare.against")}</span>
      <Select value={p.compare} onValueChange={(c: CompareMode) => p.setPeriod({ preset: p.preset, from: p.from, to: p.to, compare: c, basis: p.basis })}>
        <SelectTrigger className="h-7 w-auto gap-2 rounded-full font-mono text-sm" aria-label={t("rep.compare.against")}>
          <SelectValue>{formatDayRange(compare.from, compare.to)}</SelectValue>
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="previous">{t("rep.compare.prev")}</SelectItem>
          <SelectItem value="year">{t("rep.compare.year")}</SelectItem>
        </SelectContent>
      </Select>
      <SegmentedControl
        size="sm"
        aria-label={t("rep.compare.view")}
        value={state.compareView}
        onValueChange={state.setCompareView}
        options={[
          { value: "side", label: t("rep.compare.side") },
          { value: "delta", label: t("rep.compare.delta") },
        ]}
      />
      <span className="flex-1" />
      <span className="text-xs text-muted-foreground">
        {sortCol ? t("rep.compare.sortedBy", { metric: columnLabel(t, sortCol) }) : null}
        {live ? ` · ${t("rep.compare.live", { day: formatDayRange(p.to, p.to) })}` : ""}
      </span>
    </div>
  );
}

const KPI_KEYS = ["clicks", "leads", "cr", "revenue", "cost", "profit", "roi"];

/** The period against the other, in seven tiles (доска ReportCompare). */
export function CompareKpis({ engine }: { engine: ReportEngine }) {
  const t = useT();
  const { totals, prevTotals, schema } = engine;
  const cols = reportColumns(schema);
  return (
    <div className="grid shrink-0 grid-cols-7 overflow-hidden rounded-lg border border-border bg-card">
      {KPI_KEYS.map((k) => {
        const c = cols.find((x) => x.key === k);
        if (!c) return null;
        const cur = totals?.[k];
        const prev = prevTotals?.[k];
        const tone = deltaTone(betterOf(c, schema), cur, prev);
        const signed = k === "profit" || k === "roi";
        return (
          <MetricCard
            labelCase="none"
            key={k}
            className="rounded-none border-0 border-r border-border last:border-r-0"
            label={columnLabel(t, c)}
            loading={engine.main.isLoading}
            tone={signed && cur !== undefined ? (cur > 0 ? "ok" : cur < 0 ? "danger" : undefined) : undefined}
            value={cur === undefined ? DASH : cellText(c, cur)}
            comparison={
              <span className="font-mono text-xs">
                {t("rep.was", { value: cellText(c, prev) })}{" "}
                <span className={tone === "pos" ? "text-ok" : tone === "neg" ? "text-destructive" : "text-muted-foreground"}>{deltaText(c, cur, prev)}</span>
              </span>
            }
          />
        );
      })}
    </div>
  );
}

/** A header button drawn as its icon: square, named and hinted by its words. */
export function iconProps(iconOnly: boolean | undefined, label: string): { size?: "icon"; "aria-label"?: string; title?: string } {
  return iconOnly ? { size: "icon", "aria-label": label, title: label } : {};
}
